import { useContext, useEffect, useMemo, useState } from "react";
import BasketItemDetails from "./BasketItemDetails";
import BasketItemCard from "./BasketItemCard";
import LupaFixUpgradeBanner from "./LupaFixUpgradeBanner";
import { LanguageContext } from "../context/language/LanguageContext";
import appConst from "../Utilities/appConst.json";
import {
  bookFormatsAppMap,
  bookFormatsOnlineMap,
  bookFormatsFullMap,
  bookCoversFullMap,
} from "../Utilities/mapUtil";
import {
  basketActions,
  updateBasketItemForamt,
  updateBasketItemCover,
} from "../store/basket";
import { useDispatch, useSelector } from "react-redux";
import useBasketItemHelpers from "../hooks/useBasketItemHelpers";
import { CoverPreview } from "../shared/ui/cover-preview";
import { buildCoverPreviewData } from "../shared/ui/cover-preview/utils";
import { buildBasketItemImageUrl } from "../Utilities/basketImage";
import { getUiItemFilterPrefix } from "../Utilities/itemFilter";

const BasketItemBooks = (props) => {
  const dispatch = useDispatch();
  const showAlert = useSelector((state) => state.basket.showAlert);
  const shortPathUpgradePrice = useSelector(
    (state) => state.basket.shortPathUpgradePrice
  );
  const basket = useSelector((state) => state.basket.basket);
  const languageContext = useContext(LanguageContext);
  const title =
    languageContext.getTranslation(appConst.ALBUM) + " " + props.item.title;
  const [dropDowns, setDropDowns] = useState([]);
  const { setCountValue, handleChange } = useBasketItemHelpers({
    dispatch,
    item: props.item,
    props,
  });
  const imageUrl = useMemo(
    () => buildBasketItemImageUrl(props.item, basket),
    [props.item, basket]
  );
  const coverPreviewData = useMemo(
    () => buildCoverPreviewData(props.item, imageUrl),
    [props.item, imageUrl]
  );
  const coverPreviewContent = coverPreviewData ? (
    <div className="w-full h-full flex items-center justify-center">
      <CoverPreview
        data={coverPreviewData}
        className={coverPreviewData.aspectRatio >= 1 ? "w-full" : "h-full"}
      />
    </div>
  ) : undefined;

  const itemFormat = Number(props.item.format);
  const itemCover = Number(props.item.cover);
  const itemAlbumType = props.item.album_type;
  const matchesAlbumType = (value, type, code) =>
    String(value ?? "") === type || Number(value) === Number(code);
  const isLupaFixAlbum = matchesAlbumType(
    itemAlbumType,
    appConst.SQUARE_600,
    appConst.SQUARE_600_CODE
  );
  const isHaggadahAlbum = matchesAlbumType(
    itemAlbumType,
    appConst.HAGGADAH,
    appConst.HAGGADAH_CODE
  );

  const bookFormatsOnlineArray = Array.from(bookFormatsOnlineMap.entries()).map(
    ([key, value]) => ({
      value: key,
      label: languageContext.getTranslation(value),
      disabled: false,
    })
  );

  const bookFormatsAppArray = Array.from(bookFormatsAppMap.entries()).map(
    ([key, value]) => ({
      value: key,
      label: languageContext.getTranslation(value),
      disabled: false,
    })
  );
  useEffect(() => {
    setFormatsArrayDropDowns();
  }, [props?.item?.format, props?.item?.cover]);

  const setFormatValue = (value, is_from_alert = false) => {
    dispatch(updateBasketItemForamt(value, props.item, is_from_alert));
    dispatch(basketActions.setShowAlert(false));
  };

  const setCoverValue = (value) => {
    dispatch(updateBasketItemCover(value, props.item));
    dispatch(basketActions.setShowAlert(false));
  };

  const setFormatsArrayDropDowns = () => {
    let formatsArray = [];
    let coversArray = [];

    const isMiniLupa = itemFormat === Number(appConst.MINI_SQUARE_CODE);
    const itemFilterPrefix = getUiItemFilterPrefix(props.item?.item_filter);

    switch (itemFilterPrefix) {
      case appConst.VERSION_TREE_V1_PREFIX: // online
        formatsArray = bookFormatsOnlineArray;
        switch (itemFormat) {
          case parseInt(appConst.SMALL_SQUARE_CODE):
            if (props.item.pages >= 100) {
              coversArray = [
                {
                  value: parseInt(appConst.HARD_CODE),
                  label: languageContext.getTranslation(appConst.HARD),
                  disabled: false,
                },
                {
                  value: parseInt(appConst.SOFT_CODE),
                  label: languageContext.getTranslation(appConst.SOFT),
                  disabled: false,
                },
              ];
            } else {
              coversArray = [
                {
                  value: parseInt(appConst.HARD_CODE),
                  label: languageContext.getTranslation(appConst.HARD),
                  disabled: false,
                },
                {
                  value: parseInt(appConst.SOFT_CODE),
                  label: languageContext.getTranslation(appConst.SOFT),
                  disabled: false,
                },
                {
                  value: parseInt(appConst.LAYFLAT_CODE),
                  label: languageContext.getTranslation(appConst.LAYFLAT),
                  disabled: isHaggadahAlbum,
                },
              ];
            }
            break;
          case parseInt(appConst.LARGE_SQUARE_CODE):
            if (props.item.pages >= 100) {
              coversArray = [
                {
                  value: parseInt(appConst.HARD_CODE),
                  label: languageContext.getTranslation(appConst.HARD),
                  disabled: false,
                },
                {
                  value: parseInt(appConst.SOFT_CODE),
                  label: languageContext.getTranslation(appConst.SOFT),
                  disabled: true,
                },
              ];
            } else {
              coversArray = [
                {
                  value: parseInt(appConst.HARD_CODE),
                  label: languageContext.getTranslation(appConst.HARD),
                  disabled: false,
                },
                {
                  value: parseInt(appConst.SOFT_CODE),
                  label: languageContext.getTranslation(appConst.SOFT),
                  disabled: true,
                },
                {
                  value: parseInt(appConst.LAYFLAT_CODE),
                  label: languageContext.getTranslation(appConst.LAYFLAT),
                  disabled: isHaggadahAlbum,
                },
              ];
            }
            break;
        }
        break;
      case appConst.VERSION_TREE_V3_PREFIX: // app
        formatsArray =
          itemFormat === Number(appConst.SMALL_SQUARE_CODE) ||
          itemFormat === Number(appConst.LARGE_SQUARE_CODE)
            ? bookFormatsOnlineArray
            : bookFormatsAppArray;
        switch (itemFormat) {
          case parseInt(appConst.MINI_SQUARE_CODE):
            if (itemCover === parseInt(appConst.LAYFLAT_CODE)) {
              coversArray = [
                {
                  value: parseInt(appConst.LAYFLAT_CODE),
                  label: languageContext.getTranslation(appConst.LAYFLAT),
                  disabled: false,
                },
              ];
            } else {
              coversArray = [
                {
                  value: parseInt(appConst.HARD_CODE),
                  label: languageContext.getTranslation(appConst.HARD),
                  disabled: false,
                },
                {
                  value: parseInt(appConst.SOFT_CODE),
                  label: languageContext.getTranslation(appConst.SOFT),
                  disabled: false,
                },
              ];
            }
            break;
          case parseInt(appConst.SMALL_SQUARE_CODE):
            if (itemCover === parseInt(appConst.LAYFLAT_CODE)) {
              coversArray = [
                {
                  value: parseInt(appConst.LAYFLAT_CODE),
                  label: languageContext.getTranslation(appConst.LAYFLAT),
                  disabled: false,
                },
              ];
            } else {
              coversArray = [
                {
                  value: parseInt(appConst.HARD_CODE),
                  label: languageContext.getTranslation(appConst.HARD),
                  disabled: false,
                },
                {
                  value: parseInt(appConst.SOFT_CODE),
                  label: languageContext.getTranslation(appConst.SOFT),
                  disabled: false,
                },
              ];
            }
            break;
          case parseInt(appConst.LARGE_SQUARE_CODE):
            if (itemCover === parseInt(appConst.LAYFLAT_CODE)) {
              coversArray = [
                {
                  value: parseInt(appConst.LAYFLAT_CODE),
                  label: languageContext.getTranslation(appConst.LAYFLAT),
                  disabled: false,
                },
              ];
            } else {
              coversArray = [
                {
                  value: parseInt(appConst.HARD_CODE),
                  label: languageContext.getTranslation(appConst.HARD),
                  disabled: false,
                },
                {
                  value: parseInt(appConst.SOFT_CODE),
                  label: languageContext.getTranslation(appConst.SOFT),
                  disabled: true,
                },
              ];
            }
            break;
          default:
            coversArray = [
              {
                value: parseInt(appConst.HARD_CODE),
                label: languageContext.getTranslation(appConst.HARD),
                disabled: false,
              },
              {
                value: parseInt(appConst.SOFT_CODE),
                label: languageContext.getTranslation(appConst.SOFT),
                disabled: false,
              },
              {
                value: parseInt(appConst.LAYFLAT_CODE),
                label: languageContext.getTranslation(appConst.LAYFLAT),
                disabled: false,
              },
            ];
        }
    }

    const validFormat =
      itemFormat !== -1 &&
      formatsArray.some((format) => format.value === itemFormat);
    const validCover =
      itemCover !== -1 &&
      coversArray.some((cover) => cover.value === itemCover);

    const dropDownItems = [];

    if (!isMiniLupa) {
      dropDownItems.push({
        id: "book-format",
        array: formatsArray,
        title: languageContext.getTranslation(appConst.FORMAT),
        isDisabled:
          itemFilterPrefix === appConst.VERSION_TREE_V3_PREFIX && formatsArray !== bookFormatsOnlineArray,
        value: validFormat ? itemFormat : "",
        setValue: setFormatValue,
        placeholder: validFormat
          ? languageContext.getTranslation(
              bookFormatsFullMap.get(itemFormat)
            )
          : languageContext.getTranslation(appConst.CHOOSE_FORMAT),
        isError: showAlert && !validFormat,
      });
    }

    if (!isLupaFixAlbum) {
      dropDownItems.push({
        id: "book-cover",
        array: coversArray,
        title: languageContext.getTranslation(appConst.COVER),
        //  in the app, layflat cover and all formats other than small and mini square are disabled
        isDisabled:
          (itemFilterPrefix === appConst.VERSION_TREE_V3_PREFIX &&
            (!(
              itemFormat === Number(appConst.SMALL_SQUARE_CODE) ||
              itemFormat === Number(appConst.LARGE_SQUARE_CODE) ||
              itemFormat === Number(appConst.MINI_SQUARE_CODE)
            ) ||
              itemCover === Number(appConst.LAYFLAT_CODE))) ||
          isLupaFixAlbum,
        value: validCover ? itemCover : "",
        setValue: setCoverValue,
        placeholder: validCover
          ? languageContext.getTranslation(bookCoversFullMap.get(itemCover))
          : languageContext.getTranslation(appConst.CHOOSE_COVER),
        isError: showAlert && !validCover,
      });
    }

    setDropDowns(dropDownItems);
  };

  useEffect(() => {
    if (showAlert && dropDowns?.length > 0) {
      const formatDropdown = dropDowns.find((dropdown) => dropdown.id === "book-format");
      const coverDropdown = dropDowns.find((dropdown) => dropdown.id === "book-cover");

      const validFormat =
        !formatDropdown ||
        (itemFormat !== -1 &&
          formatDropdown.array.some((format) => format.value === itemFormat));
      const validCover =
        coverDropdown &&
        itemCover !== -1 &&
        coverDropdown.array.some((cover) => cover.value === itemCover);

      const validationConfig = {
        "book-format": { isValid: validFormat, label: appConst.FORMAT },
        "book-cover": { isValid: validCover, label: appConst.COVER },
      };

      setDropDowns(
        dropDowns.map((dropdown) => {
          const config = validationConfig[dropdown.id];
          if (config) {
            return {
              ...dropdown,
              isError: showAlert && !config.isValid,
              helperText: `${languageContext.getTranslation(
                appConst.VALIDATION_TEXT_REQUIRED_FIELD
              )} ${languageContext.getTranslation(config.label)}`,
            };
          }
          return dropdown;
        })
      );
    }
  }, [showAlert]);

  const handleUpgrade = () => {
    setFormatValue(+appConst.LARGE_SQUARE_CODE, true);
  };

  const handleDowngrade = () => {
    setFormatValue(+appConst.SMALL_SQUARE_CODE, true);
  };

  const upgradeLabel = isLupaFixAlbum
    ? languageContext.getTranslation(appConst.LUPA_FIX)
    : undefined;

  const isSmallSquare = itemFormat === +appConst.SMALL_SQUARE_CODE;
  const isLargeSquare = itemFormat === +appConst.LARGE_SQUARE_CODE;
  const isUpgradedFormat = isLargeSquare;

  const showUpgradeBanner =
    isLupaFixAlbum && (isSmallSquare || isLargeSquare);

  return (
    <BasketItemCard
      imageUrl={imageUrl}
      imageAlt={title}
      imageContent={coverPreviewContent}
      upgradeLabel={upgradeLabel}
      showCheckbox={props.isDeletionMode}
      isChecked={props.isChecked}
      onToggle={handleChange}
      quantity={props.item.quantity}
      setCountValue={setCountValue}
      dropDowns={dropDowns}
      item={props.item}
      tuneSettingsTitle={props.tuneSettingsTitle || title}
      tuneSettingsFooter={props.tuneSettingsFooter}
      tuneSettingsMobileFooter={props.tuneSettingsMobileFooter}
      upgradeBanner={
        showUpgradeBanner ? (
          <LupaFixUpgradeBanner
            price={shortPathUpgradePrice}
            onUpgrade={handleUpgrade}
            onDowngrade={handleDowngrade}
            isUpgraded={isUpgradedFormat}
          />
        ) : undefined
      }
    >
      <BasketItemDetails
        quantity={props.item.quantity}
        item={props.item}
        dropDowns={dropDowns}
        title={title}
        pagesNumber={props.item.pages}
        isShow={props.item.is_valid}
        imageUrl={imageUrl}
        tuneSettingsTitle={props.tuneSettingsTitle || title}
        tuneSettingsFooter={props.tuneSettingsFooter}
        tuneSettingsMobileFooter={props.tuneSettingsMobileFooter}
        setCountValue={setCountValue}
        upgradeBanner={
          showUpgradeBanner ? (
            <LupaFixUpgradeBanner
              price={shortPathUpgradePrice}
              onUpgrade={handleUpgrade}
              onDowngrade={handleDowngrade}
              isUpgraded={isUpgradedFormat}
            />
          ) : undefined
        }
      />
    </BasketItemCard>
  );
};
export default BasketItemBooks;
